import { BaseGet, BasePost, BaseDeleteVoid, BasePut } from './BaseData';
import { appSettings } from '../AppSettings';
import { GridColumnVisibilityModel, GridSortModel, GridFilterModel, GridLogicOperator } from '@mui/x-data-grid-pro';
import { v4 as uuidv4 } from 'uuid';

export type PublicPermission = 'Read' | 'Write' | 'Admin';

interface GridViewPermission {
  owner?: string;
  isPublic: boolean;
  publicPermission?: PublicPermission;
  canRead?: boolean;
  canWrite?: boolean;
  canAdmin?: boolean;
}

export interface GridSortOption {
  field: string;
  sort: 'asc' | 'desc';
}

export interface GridColumnVisibility {
  [key: string]: boolean;
}

export interface GridFilterItem {
  id: string;
  field: string;
  value: string;
  operator: string;
}

export interface GridFilterOption {
  items: GridFilterItem[];
  logicalOperator: string;
}

export interface GridLayout extends GridViewPermission {
  gridLayoutId?: string;
  name: string;
  sortOptions: GridSortModel;
  columnVisibility: GridColumnVisibilityModel;
  columnOrder: string[];
}

export interface GridFilter extends GridViewPermission {
  gridFilterId?: string;
  name: string;
  filterOptions: GridFilterModel;
}

export interface GridComponent {
  gridComponentId?: string;
  componentName: string;
  transitionEnumId?: string;
  configuredTransitionId?: string;
  researchProjectId?: string;
}

export interface GridViewState {
  sortOptions: GridSortModel;
  columnVisibility: GridColumnVisibilityModel;
  columnOrder: string[];
  filterOptions: GridFilterModel;
}

export interface ComponentGridViewModel extends GridViewState {
  currentGridComponentId: string | undefined;
  currentGridLayoutId: string | undefined;
  currentGridFilterId: string | undefined;
}

export const defaultFilterOptions: GridFilterModel = {
  items: [],
  logicOperator: GridLogicOperator.And,
};

export const defaultComponentGridViewModel: ComponentGridViewModel = {
  currentGridComponentId: undefined,
  currentGridLayoutId: undefined,
  currentGridFilterId: undefined,
  sortOptions: [],
  columnVisibility: {},
  columnOrder: [],
  filterOptions: defaultFilterOptions,
};

const BASE_URL = `${appSettings.api.endpoint}/api/v2/gridview`;

export async function GetGridLayouts(accessToken: string, gridComponentId: string): Promise<GridLayout[]> {
  return await BaseGet(`${BASE_URL}/components/${gridComponentId}/layouts`, accessToken);
}

export async function CreateGridLayout(
  accessToken: string,
  gridComponentId: string,
  layout: GridLayout
): Promise<GridLayout> {
  return await BasePost(`${BASE_URL}/components/${gridComponentId}/layouts`, layout, accessToken);
}

export async function UpdateGridLayout(
  accessToken: string,
  gridLayoutId: string,
  layout: GridLayout
): Promise<GridLayout> {
  return await BasePut(`${BASE_URL}/layouts/${gridLayoutId}`, layout, accessToken);
}

export async function DeleteGridLayout(accessToken: string, gridLayoutId: string): Promise<void> {
  return await BaseDeleteVoid(`${BASE_URL}/layouts/${gridLayoutId}`, accessToken);
}

export async function GetGridFilters(accessToken: string, gridComponentId: string): Promise<GridFilter[]> {
  return await BaseGet(`${BASE_URL}/components/${gridComponentId}/filters`, accessToken);
}

export async function CreateGridFilter(
  accessToken: string,
  gridComponentId: string,
  filter: GridFilter
): Promise<GridFilter> {
  return await BasePost(`${BASE_URL}/components/${gridComponentId}/filters`, filter, accessToken);
}

export async function UpdateGridFilter(
  accessToken: string,
  gridFilterId: string,
  filter: GridFilter
): Promise<GridFilter> {
  return await BasePut(`${BASE_URL}/filters/${gridFilterId}`, filter, accessToken);
}

export async function DeleteGridFilter(accessToken: string, gridFilterId: string): Promise<void> {
  return await BaseDeleteVoid(`${BASE_URL}/filters/${gridFilterId}`, accessToken);
}

export async function GetGridComponents(accessToken: string, componentName: string): Promise<GridComponent[]> {
  const params = new URLSearchParams({ componentName: componentName });

  return await BaseGet(`${BASE_URL}/components/search?${params.toString()}`, accessToken);
}

export async function CreateGridComponent(accessToken: string, component: GridComponent): Promise<GridComponent> {
  return await BasePost(`${BASE_URL}/components`, component, accessToken);
}

export async function GetGridViews(
  accessToken: string,
  gridComponentId: string
): Promise<{ gridLayouts: GridLayout[]; gridFilters: GridFilter[] }> {
  const [layouts, filters] = await Promise.all([
    GetGridLayouts(accessToken, gridComponentId),
    GetGridFilters(accessToken, gridComponentId),
  ]);

  return {
    gridLayouts: layouts,
    gridFilters: filters,
  };
}

export function ensureFilterItemsHaveIds(filterModel: GridFilterModel): GridFilterModel {
  if (!filterModel || !filterModel.items) {
    return filterModel;
  }

  return {
    ...filterModel,
    items: filterModel.items.map(item => ({
      ...item,
      id: item.id || uuidv4(),
    })),
  };
}
